import { DeviceTokenHelper } from '@/lib/helpers/device-token.helper'
import { getValue } from '@/lib/utils'
import { LocalStorage } from '@/lib/utils/local-storage'
import { ApiResponse, httpClient } from '@/services/client/http-client'
import type {
  AuthResponse,
  LoginDto,
  RegisterDto,
  TConfirmInfoUserBody,
  TConfirmInfoUserResponse,
  TDataAuth,
  TForgotPasswordBody,
  TForgotVerifyOtpParams,
  TRegisterResponse,
  TResetPasswordBody,
  TSendOtpParams,
  TSendOtpResponse,
  TVerifyOtpAuthParams,
  TVerifyOtpAuthResponse,
  TVerifyOtpParams,
  TVerifyOtpResponse,
} from '@/types/auth'
import { deleteCookie } from 'cookies-next'
import { AuthHelper } from '../helper/__h.auth'

export const __cAuthService = {
  login: async (data: LoginDto) => {
    const response = await httpClient.V4.post<ApiResponse<AuthResponse>>(
      '/auth/login',
      {
        ...data,
        device_token: DeviceTokenHelper.get(),
      }
    )
    // Nếu không thành công thì throw message
    if (!response.state || response?.status !== 200) {
      throw new Error(response.message)
    }

    // Nếu đã verify phone thì lưu token vào cookie
    if (response.data?.user?.token && response.data?.user?.device_token) {
      AuthHelper.saveDataAfterLogin({
        token: response.data?.user.token,
        device_token: response.data?.user.device_token,
        user_info: response.data?.user,
      })

      // Thiết lập Authorization header ngay cho client
      httpClient.V1.setAuthToken(response.data?.user.token)
      httpClient.V2.setAuthToken(response.data?.user.token)
      httpClient.V3.setAuthToken(response.data?.user.token)
      httpClient.V4.setAuthToken(response.data?.user.token)
      httpClient.V4_NEW.setAuthToken(response.data?.user.token)
    }

    return response
  },

  register: async (data: RegisterDto) => {
    return httpClient.V4.post<ApiResponse<TRegisterResponse>>(
      '/auth/register',
      data
    )
  },

  logout: () => {
    try {
      // Xử lý clear token
      deleteCookie(process.env.NEXT_PUBLIC_TOKEN_KEY)
      deleteCookie('user_info')

      LocalStorage.clearWithoutKey('device_token')
      // return httpClient.V1.post('/auth/logout')
    } catch (error) {
      console.error('🚀 ~ logout ~ error:', error)
    }
  },

  sendOtp: async (address: string, params: TSendOtpParams) => {
    return httpClient.V2.get<TSendOtpResponse>(`/otp/send/${address}/`, {
      params,
    })
  },

  verifyOtp: async (body: TVerifyOtpParams) => {
    return httpClient.V2.post<TVerifyOtpResponse>(`/otp/verify`, body)
  },

  // Nếu chưa verify thì khi verify-> gọi OTP thì sẽ trẻ token để login luôn
  verifyOtpAuth: async (body: TVerifyOtpAuthParams) => {
    const response = await httpClient.V4.post<TVerifyOtpAuthResponse>(
      `/auth/verify-otp/`,
      body
    )

    if (!response.state || response?.status !== 200) {
      throw new Error(response.message || 'Có lỗi xảy ra')
    }

    const token = getValue(response, 'data.user.token', '')
    if (token) {
      AuthHelper.saveDataAfterLogin({
        token,
        device_token: getValue(response, 'data.user.device_token', ''),
        user_info: getValue(response, 'data.user', null),
      })
    } else {
      console.error('Token is not found')
    }

    return response
  },

  resetPassword: async (body: TResetPasswordBody) => {
    return httpClient.V4_NEW.post<ApiResponse<Record<string, never>>>(
      '/account/reset-password',
      body
    )
  },

  forgotPassword: async (body: TForgotPasswordBody) => {
    const response = await httpClient.V4_NEW.post<
      ApiResponse<{ delay: number }>
    >('/account/forgot-password', body)

    if (response.state === false) {
      throw new Error(response.message || 'Có lỗi xảy ra')
    }

    return response
  },

  forgotVerifyOtp: async (body: TForgotVerifyOtpParams) => {
    const response = await httpClient.V4_NEW.post<
      ApiResponse<{ forgot_code: string }>
    >('/account/otp/verify-otp-forgot-password', body)

    if (response.state === false) {
      throw new Error(response.message || 'Có lỗi xảy ra')
    }

    return response
  },

  confirmInfoUser: async (body: TConfirmInfoUserBody) => {
    const response = await httpClient.V4.post<
      TDataAuth<TConfirmInfoUserResponse>
    >('/auth/confirm-info', body)

    if (!response.state || response?.status !== 200) {
      throw new Error(response.message || 'Có lỗi xảy ra')
    }

    return response
  },

  // Login third party
  loginThirdParty: async (provider: 'google' | 'facebook') => {
    const response = await httpClient.V1.get<string>(`/redirect/${provider}`)
    return response
  },

  // Xử lý get data từ callback
  callbackThirdParty: async (provider: 'google' | 'facebook', code: string) => {
    const response = await httpClient.V4.post<TDataAuth<AuthResponse>>(
      `/auth/callback/${provider}`,
      { code }
    )

    // Nếu không thành công thì throw message
    if (!response.state || response?.status !== 200) {
      throw new Error(response.message)
    }

    // Nếu đã verify phone thì lưu token vào cookie
    if (
      (response.data?.token && response.data?.device_token) ||
      (response.data?.user?.token && response.data?.user?.device_token)
    ) {
      AuthHelper.saveDataAfterLogin({
        token: response.data?.token ?? response.data?.user?.token ?? '',
        device_token:
          response.data?.device_token ??
          response.data?.user?.device_token ??
          '',
        user_info: response.data?.user,
      })

      const token = response.data?.token ?? response.data?.user?.token ?? ''
      if (token) {
        httpClient.V1.setAuthToken(token)
        httpClient.V2.setAuthToken(token)
        httpClient.V3.setAuthToken(token)
        httpClient.V4.setAuthToken(token)
        httpClient.V4_NEW.setAuthToken(token)
      }
    }

    return response
  },
}
